/**
 * Notification state shared by the bell badge and the banner stack.
 *
 * These two surfaces render in different subtrees — the banners are siblings
 * above the page in App.tsx, the bell lives inside the dashboard header — but
 * they must never disagree. Dismissing a banner has to decrement the badge in
 * the same render. So they share one provider, one fetch, and one dismiss
 * action, and every mutation takes its new unread count from the server's
 * response rather than decrementing locally.
 *
 * Failure is always silent: if the fetch fails the provider reports zero
 * notifications and renders nothing. A notification system must never be able
 * to break the dashboard around it.
 *
 * Reference: Notification-System.md
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchWithAuthRecovery } from '../lib/fetch-with-auth-recovery';

const API_URL = import.meta.env.VITE_API_URL;

/** Background refresh cadence. Notifications are rare; this is a safety net. */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export type NotificationSeverity = 'info' | 'warning' | 'critical';

/** An unread, unexpired notification — one banner in the stack. */
export interface ActiveNotification {
  id: string;
  title: string;
  body: string;
  severity: NotificationSeverity;
  link_url: string | null;
  link_label: string | null;
  created_at: string;
}

/** Any notification in the history list, read or not, expired or not. */
export interface HistoryNotification extends ActiveNotification {
  is_read: boolean;
  read_at: string | null;
  is_expired: boolean;
}

interface NotificationsResponse {
  success: boolean;
  unread_count?: number;
  active?: ActiveNotification[];
  history?: HistoryNotification[];
  error?: string;
}

interface MutationResponse {
  success: boolean;
  updated?: number;
  unread_count?: number;
  error?: string;
}

interface NotificationsContextValue {
  unreadCount: number;
  active: ActiveNotification[];
  history: HistoryNotification[];
  loading: boolean;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const EMPTY: NotificationsContextValue = {
  unreadCount: 0,
  active: [],
  history: [],
  loading: false,
  markRead: async () => {},
  markAllRead: async () => {},
  refresh: async () => {},
};

const NotificationsContext = createContext<NotificationsContextValue>(EMPTY);

/**
 * Read notification state. Safe to call outside a provider — returns an empty,
 * inert value rather than throwing, so a component can be dropped onto a page
 * that has no provider without crashing it.
 */
export function useNotifications(): NotificationsContextValue {
  return useContext(NotificationsContext);
}

export function NotificationsProvider({
  locationId,
  children,
}: {
  locationId: string | null;
  children: ReactNode;
}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [active, setActive] = useState<ActiveNotification[]>([]);
  const [history, setHistory] = useState<HistoryNotification[]>([]);
  const [loading, setLoading] = useState(false);

  // Guards against a slow in-flight response overwriting newer state after a
  // dismissal, and against setting state on an unmounted provider.
  const requestSeq = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!locationId) return;

    const seq = ++requestSeq.current;
    setLoading(true);

    try {
      const response = await fetchWithAuthRecovery(
        `${API_URL}/dashboard/${locationId}/notifications`,
        { credentials: 'include' }
      );

      const data = (await response.json()) as NotificationsResponse;

      // A response that lost the race to a newer request is discarded.
      if (!mounted.current || seq !== requestSeq.current) return;

      if (data.success) {
        setUnreadCount(data.unread_count ?? 0);
        setActive(data.active ?? []);
        setHistory(data.history ?? []);
      }
    } catch {
      // Silent by design — see the file header. Leave prior state intact so a
      // transient network blip doesn't blank out banners the user was reading.
    } finally {
      if (mounted.current && seq === requestSeq.current) {
        setLoading(false);
      }
    }
  }, [locationId]);

  // Initial load + refresh whenever the location changes.
  useEffect(() => {
    if (!locationId) {
      setUnreadCount(0);
      setActive([]);
      setHistory([]);
      return;
    }
    void refresh();
  }, [locationId, refresh]);

  // Refresh when the tab regains focus, and on a slow interval.
  useEffect(() => {
    if (!locationId) return;

    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    const timer = window.setInterval(() => {
      void refresh();
    }, REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.clearInterval(timer);
    };
  }, [locationId, refresh]);

  const markRead = useCallback(
    async (notificationId: string) => {
      if (!locationId) return;

      // Optimistic: drop it from the banner stack and flag it read in history
      // immediately, so the UI responds to the click without waiting.
      setActive((prev) => prev.filter((n) => n.id !== notificationId));
      setHistory((prev) =>
        prev.map((n) =>
          n.id === notificationId && !n.is_read
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      try {
        const response = await fetchWithAuthRecovery(
          `${API_URL}/dashboard/${locationId}/notifications/${notificationId}/read`,
          { method: 'POST', credentials: 'include' }
        );

        const data = (await response.json()) as MutationResponse;

        // Server truth wins over the optimistic decrement. This is what keeps
        // two open tabs from drifting.
        if (mounted.current && data.success && typeof data.unread_count === 'number') {
          setUnreadCount(data.unread_count);
        }
      } catch {
        // The optimistic update stands; the next refresh reconciles it.
      }
    },
    [locationId]
  );

  const markAllRead = useCallback(async () => {
    if (!locationId) return;

    const nowIso = new Date().toISOString();
    const clearedIds = new Set(active.map((n) => n.id));

    setActive([]);
    setUnreadCount(0);
    setHistory((prev) =>
      prev.map((n) =>
        clearedIds.has(n.id) ? { ...n, is_read: true, read_at: nowIso } : n
      )
    );

    try {
      await fetchWithAuthRecovery(
        `${API_URL}/dashboard/${locationId}/notifications/read-all`,
        { method: 'POST', credentials: 'include' }
      );
    } catch {
      // Next refresh reconciles.
    }
  }, [locationId, active]);

  return (
    <NotificationsContext.Provider
      value={{ unreadCount, active, history, loading, markRead, markAllRead, refresh }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}
